import { makeAutoObservable } from "mobx";
import { RuleProps } from "./features/RuleRow";
import { ConditionProps } from "./components/ConditionRow";
import {
  ColumnSourceEnum,
  ProcessingRuleOUT,
  PropertyBrowserTreeNode,
  processingRulesApi,
} from "api-client";
import { PROCESSING_RULE_OPERATORS } from "features/MappingTools/DynamicMappingEditor/const";
import { EMPTY_CONDITION } from "./features/RuleConditions";

class RulesSetStore {
  currentRule: RuleProps | undefined = undefined;
  rulesList: RuleProps[] = [];
  properties: PropertyBrowserTreeNode[] = [];
  organisationID: string | undefined = undefined;
  openNoRulePopup: boolean = false;
  loading: boolean = true;

  setCurrentRule(currentRule: RuleProps | undefined) {
    if (currentRule) {
      this.currentRule = {
        ...currentRule,
        conditions: currentRule.conditions.map((rec) => ({
          ...rec,
          valueProperty: rec.valueProperty
            ? { ...rec.valueProperty }
            : undefined,
          property: rec.property ? { ...rec.property } : undefined,
        })),
      };
    } else {
      this.currentRule = undefined;
    }
  }

  setRulesList(rulesList: RuleProps[]) {
    this.rulesList = rulesList;
  }

  setProperties(properties: PropertyBrowserTreeNode[]) {
    this.properties = properties;
  }

  async setRuleConditions(conditions: ConditionProps[]) {
    if (this.currentRule) {
      this.setCurrentRule({ ...this.currentRule, conditions });
    }
  }

  setOrganisationID(organisationID: string | undefined) {
    this.organisationID = organisationID;
  }

  setOpenNoRulePopup(openNoRulePopup: boolean) {
    this.openNoRulePopup = openNoRulePopup;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  isThereAnyValidRule() {
    return this.rulesList
      .map((item) => item.conditions)
      .flat()
      .map((rec) => rec.property)
      .some(Boolean);
  }

  private isAnEmptyCondition(condition: ConditionProps | undefined) {
    return (
      !condition?.property && !condition?.value && !condition?.valueProperty
    );
  }

  private conditionHasAnyUpdate(conditionIndex: number) {
    const savedItem = this.rulesList.find(
      (item) => item.id === this.currentRule?.id
    );
    const saveCondition = savedItem?.conditions[conditionIndex];
    const currentCondition = this.currentRule?.conditions[conditionIndex];
    const newConditionAdded =
      Number(savedItem?.conditions.length) <
        Number(this.currentRule?.conditions.length) &&
      this.isAnEmptyCondition(currentCondition);
    if (newConditionAdded) {
      return false;
    }
    const hasAnyDifference =
      saveCondition?.property?.column_key !=
        currentCondition?.property?.column_key ||
      saveCondition?.operator != currentCondition?.operator ||
      (!saveCondition?.valueProperty?.column_key &&
        String(saveCondition?.value) != String(currentCondition?.value)) ||
      saveCondition?.valueProperty?.column_key !=
        currentCondition?.valueProperty?.column_key;
    return hasAnyDifference || !currentCondition;
  }

  async checkForUpdateRule(
    conditionIndex: number | undefined,
    update?: boolean
  ) {
    if (!this.currentRule || conditionIndex === undefined) return;
    const isValidConditionToUpdate =
      !update && this.conditionHasAnyUpdate(conditionIndex);
    if (update || isValidConditionToUpdate) {
      await this.updateRule(this.currentRule);
    }
  }

  hasRuleError(rule: RuleProps | undefined) {
    if (!rule) return false;
    const hasError =
      rule.conditions.filter(
        (item) =>
          item.property &&
          !item.value &&
          !item.valueProperty &&
          !item.operator?.includes("EMPTY")
      ).length > 0;
    return hasError;
  }

  checkConditionsValidation() {
    const result = this.rulesList.map((item) => {
      item.conditions.forEach((item) => {
        if (item.property && !item.value) {
          item.hasError = true;
        }
      });
      return item.conditions?.filter(
        (item) =>
          item.property &&
          !item.value &&
          !item.valueProperty &&
          !item.operator?.includes("EMPTY")
      );
    });
    const validationOk = result.flat().length === 0;
    return validationOk;
  }

  reset() {
    this.currentRule = undefined;
    this.rulesList = [];
    this.loading = true;
  }

  private transformRule(item: ProcessingRuleOUT) {
    return {
      id: item.id,
      name: item.name,
      conditions: item.filter.rules.length
        ? item.filter.rules.map((condition) => {
            return {
              property: this.findPropertyRecursively(
                this.properties,
                condition.column_key
              ),
              operator: PROCESSING_RULE_OPERATORS.find(
                (op) => op.id === condition.operator
              )?.id,
              value:
                condition.value_source === ColumnSourceEnum.Literal
                  ? (condition.value as string) ?? ""
                  : "",
              valueProperty:
                condition.value_source === ColumnSourceEnum.TemplateVariable
                  ? this.findPropertyRecursively(
                      this.properties,
                      condition.value as string
                    )
                  : condition.value_source != ColumnSourceEnum.Literal
                  ? this.findPropertyRecursively(
                      this.properties,
                      condition.value as string
                    )
                  : undefined,
              value_source: condition.value_source,
              hasError: condition.operator && !condition.value,
            };
          })
        : [EMPTY_CONDITION],
    };
  }

  private findPropertyRecursively(
    properties: PropertyBrowserTreeNode[],
    current_column_key: string
  ): PropertyBrowserTreeNode | undefined {
    for (const item of properties) {
      if (item.column_key === current_column_key) {
        return item;
      }
      if (item.children) {
        const found = this.findPropertyRecursively(
          item.children,
          current_column_key
        );
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  async fetchRulesList(organisation_id?: string | null) {
    const orgID = organisation_id ?? this.organisationID;
    if (!orgID) return;
    await processingRulesApi()
      .mappingEditorApiV1RoutersProcessingRulesListProcessingRules(orgID)
      .then((res) => {
        const transformedData = res.data.map((item) => {
          return this.transformRule(item);
        });
        this.setRulesList([...transformedData]);
      })
      .catch((err) => console.error(err, "ProcessingRulesListProcessingRules"));
  }

  async createNewRule(name: string) {
    if (!this.organisationID) return;
    await processingRulesApi()
      .mappingEditorApiV1RoutersProcessingRulesCreateAProcessingRule({
        organisation_id: this.organisationID,
        processing_rule: {
          name: name,
          action: "manual_match",
          filter: {
            logical_operator: "AND",
            rules: [],
          },
        },
      })
      .catch((err) =>
        console.error(err, "ProcessingRulesCreateAProcessingRule")
      );
  }

  async removeRule(ruleID: number) {
    await processingRulesApi()
      .mappingEditorApiV1RoutersProcessingRulesDeleteProcessingRule(ruleID)
      .catch((err) => console.error(err, "ProcessingRulesDeleteProcessingRule"))
      .finally(() => this.fetchRulesList());
  }

  async updateRule(rule: RuleProps | undefined, name?: string) {
    if (!rule) return;
    const ruleConditions = this.getFilteringRuleList(rule);
    await processingRulesApi()
      .mappingEditorApiV1RoutersProcessingRulesUpdateProcessingRule(rule.id, {
        name: name ?? rule.name,
        filter: {
          logical_operator: "AND",
          rules: ruleConditions,
        },
        action: "manual_match",
      })
      .catch((err) => console.error(err, "ProcessingRulesUpdateProcessingRule"))
      .finally(async () => await this.fetchRulesList());
  }

  private getFilteringRuleList(rule: RuleProps) {
    return rule.conditions
      .map((item) => {
        if (item.property) {
          return {
            column_key: item.property.column_key,
            column_source: item.property.column_source,
            operator: item.operator,
            value: item.valueProperty
              ? item.valueProperty?.column_key || null
              : item.value || null,
            value_source: item.valueProperty
              ? item.value_source
              : ColumnSourceEnum.Literal,
          };
        }
      })
      .filter(Boolean) as [];
  }

  async fetchProperties() {
    if (!this.organisationID) return;
    await processingRulesApi()
      .mappingEditorApiV1RoutersProcessingRulesOrganisationalPropertyBrowser(
        this.organisationID
      )
      .then((res) => this.setProperties(res.data))
      .catch((err) =>
        console.error(err, "ProcessingRulesOrganisationalPropertyBrowser")
      );
  }

  async updateRulesOrders() {
    if (this.rulesList.length === 0) return;
    await processingRulesApi().mappingEditorApiV1RoutersProcessingRulesUpdateProcessingRuleOrders(
      this.rulesList.map((item, index) => ({
        processing_rule_id: item.id,
        order: index + 1,
      }))
    );
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: RulesSetStore;

  static getInstance(): RulesSetStore {
    if (!RulesSetStore.instance) {
      RulesSetStore.instance = new RulesSetStore();
    }
    return RulesSetStore.instance;
  }
}

export const rulesSetStore = RulesSetStore.getInstance();
